import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  Divider,
  TextField,
  Button,
  Grid,
} from "@mui/material";
import React, { useState } from "react";
import useApiEp from "../hooks/useApiEp";

const CreditUsageHistory = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: "",
    endDate: "",
  });

  const queryParams = new URLSearchParams();
  if (appliedFilters.startDate)
    queryParams.append("startDate", appliedFilters.startDate);
  if (appliedFilters.endDate)
    queryParams.append("endDate", appliedFilters.endDate);
  const queryString = queryParams.toString();

  const {
    data: customersUsage,
    isLoading,
    isError,
    error,
  } = useApiEp(
    `/credit/get-credit-usage-all-users${queryString ? `?${queryString}` : ""}`
  );

  const inrFormat = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);

  const handleApplyFilter = () => setAppliedFilters({ startDate, endDate });

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setAppliedFilters({ startDate: "", endDate: "" });
  };

  // Group by customerId, sort each group by highest usage
  const groupedData = (() => {
    if (!customersUsage?.history) return [];

    const groups = {};
    for (const item of customersUsage.history) {
      const key = item.customerId || "(Unknown)";
      if (!groups[key]) groups[key] = [];
      groups[key].push({
        phoneNumberId: item.phoneNumberId || "(No Phone)",
        totalUsage: item.totalUsage,
      });
    }

    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => b.totalUsage - a.totalUsage);
    }

    return Object.entries(groups)
      .map(([customerId, rows]) => ({
        customerId,
        rows,
        groupTotal: rows.reduce((s, r) => s + r.totalUsage, 0),
      }))
      .sort((a, b) => b.groupTotal - a.groupTotal);
  })();

  // Sort nonPhoneIdHistory by totalUsage desc
  const aiCreditsData = (() => {
    if (!customersUsage?.nonPhoneIdHistory) return [];
    return [...customersUsage.nonPhoneIdHistory].sort(
      (a, b) => b.totalUsage - a.totalUsage
    );
  })();

  const totalUsageSum = customersUsage?.history?.reduce(
    (sum, item) => sum + item.totalUsage,
    0
  );

  const totalAiCreditsSum = aiCreditsData.reduce(
    (sum, item) => sum + item.totalUsage,
    0
  );

  const dateRangeLabel = (
    <>
      {appliedFilters.startDate && `from ${appliedFilters.startDate}`}
      {appliedFilters.startDate && appliedFilters.endDate && " "}
      {appliedFilters.endDate && `to ${appliedFilters.endDate}`}
    </>
  );

  let rowCounter = 0;

  return (
    <Container sx={{ mt: 3 }}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Credit Usage – All Users
      </Typography>

      {/* Date Range Filter */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Filter by Date Range
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box display="flex" gap={1}>
              <Button variant="contained" onClick={handleApplyFilter} fullWidth>
                Apply
              </Button>
              <Button variant="outlined" onClick={handleReset} fullWidth>
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {isLoading && (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Typography color="error" mt={3}>
          {error?.message || "Something went wrong"}
        </Typography>
      )}

      {!isLoading && !isError && (
        <>
          {/* ── Phone Number Usage Section ── */}
          <Typography variant="h6" fontWeight={600} mb={1}>
            Phone Number Usage
          </Typography>

          <Paper
            elevation={3}
            sx={{
              mb: 3,
              p: 2,
              background: "#f9fafb",
              borderLeft: "6px solid #1976d2",
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Total Usage
              {(appliedFilters.startDate || appliedFilters.endDate) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  ml={1}
                >
                  ({dateRangeLabel})
                </Typography>
              )}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="h5" color="success.main" fontWeight={700}>
              {inrFormat(totalUsageSum || 0)}
            </Typography>
          </Paper>

          <TableContainer component={Paper} elevation={3} sx={{ mb: 6 }}>
            <Table>
              <TableHead sx={{ background: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Phone Number ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Usage (INR)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Group Total (INR)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedData.map((group) => {
                  rowCounter++;
                  const currentCounter = rowCounter;

                  return group.rows.map((row, rowIndex) => (
                    <TableRow
                      key={`${group.customerId}-${rowIndex}`}
                      hover
                      sx={{
                        ...(rowIndex === 0 && {
                          borderTop: "2px solid #e0e0e0",
                        }),
                      }}
                    >
                      {rowIndex === 0 && (
                        <>
                          <TableCell
                            rowSpan={group.rows.length}
                            sx={{ fontWeight: 600, verticalAlign: "middle" }}
                          >
                            {currentCounter}
                          </TableCell>
                          <TableCell
                            rowSpan={group.rows.length}
                            sx={{ fontWeight: 600, verticalAlign: "middle" }}
                          >
                            {group.customerId}
                          </TableCell>
                        </>
                      )}
                      <TableCell>{row.phoneNumberId}</TableCell>
                      <TableCell>{inrFormat(row.totalUsage)}</TableCell>
                      {rowIndex === 0 && (
                        <TableCell
                          rowSpan={group.rows.length}
                          sx={{
                            fontWeight: 700,
                            color: "success.main",
                            verticalAlign: "middle",
                          }}
                        >
                          {inrFormat(group.groupTotal)}
                        </TableCell>
                      )}
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── AI Credits Section ── */}
          <Typography variant="h6" fontWeight={600} mb={1}>
            AI Credits Usage
          </Typography>

          <Paper
            elevation={3}
            sx={{
              mb: 3,
              p: 2,
              background: "#f9fafb",
              borderLeft: "6px solid #9c27b0",
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Total AI Credits Used
              {(appliedFilters.startDate || appliedFilters.endDate) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  ml={1}
                >
                  ({dateRangeLabel})
                </Typography>
              )}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="h5" color="secondary.main" fontWeight={700}>
              {inrFormat(totalAiCreditsSum)}
            </Typography>
          </Paper>

          <TableContainer component={Paper} elevation={3} sx={{ mb: 6 }}>
            <Table>
              <TableHead sx={{ background: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Usage (INR)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aiCreditsData.map((item, index) => (
                  <TableRow key={item.customerId} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {item.customerId}
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, color: "secondary.main" }}
                    >
                      {inrFormat(item.totalUsage)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Container>
  );
};

export default CreditUsageHistory;
